import React, { useContext, useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { OrderDirection, UserFilterResponse } from '../../Models/Models';
import { SiteContext, UserContext } from '../../Contexts';
import { EntityColors } from '../../Themes/EntityColors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import CustomTooltip from '../Tooltips/CustomTooltip';
import EditButton from '../Buttons/EditButton';
import { Utils } from '../../Services/Utils';
import { UserService } from '../../Services/UserService';
import TableShell from '../Table/TableShell';
import TableToolbar from '../Table/TableToolbar';

const UserTableComponent = () => {
    useEffect(() => {
        Utils.setDocumentTitle('Používatelia');
    }, []);

    const { filterUsers } = UserService();
    const [response, setResponse] = useState<UserFilterResponse | null>(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(6);
    const [orderDirection, setOrderDirection] = useState<OrderDirection>('Desc');
    const navigate = useNavigate();
    const { currentSite }: any = useContext(SiteContext);
    const { currentUser }: any = useContext(UserContext);
    const { action, icon } = EntityColors.user;

    const loadPage = async (pageNum: number, opts?: { perPage?: number; order?: OrderDirection }) => {
        const limit = opts?.perPage ?? perPage;
        const resp = await filterUsers({
            limit,
            offset: (pageNum - 1) * limit,
            orderDirection: opts?.order ?? orderDirection,
        });
        setResponse(resp ?? null);
    };

    const changePerPage = (count: number) => {
        setPerPage(count);
        setPage(1);
        void loadPage(1, { perPage: count });
    };

    const changeOrder = (dir: OrderDirection) => {
        setOrderDirection(dir);
        setPage(1);
        void loadPage(1, { order: dir });
    };

    useEffect(() => {
        if (!currentUser.isSuperAdmin) {
            navigate('/404');
            return;
        }
        setPage(1);
        void loadPage(1);
    }, [currentSite]);

    return (
        <TableShell
            title="Používatelia"
            icon={icon}
            accentColor={action}
            createHref="/user/create"
            isLoading={response === null}
            isEmpty={response !== null && response.data.length === 0}
            emptyMessage="Žiadni používatelia"
            page={page}
            totalCount={response?.totalCount}
            perPage={perPage}
            onPageChange={p => { setPage(p); loadPage(p); }}
            toolbar={
                <TableToolbar
                    totalCount={response?.totalCount ?? 0}
                    nounForms={['používateľ', 'používatelia', 'používateľov']}
                    perPage={perPage}
                    onPerPageChange={changePerPage}
                    orderDirection={orderDirection}
                    onOrderChange={changeOrder}
                />
            }
        >
            <Table responsive>
                <thead>
                    <tr>
                        <th>Meno</th>
                        <th>Email</th>
                        <th>SuperAdmin</th>
                        <th>Weby</th>
                        <th>Dočasné heslo</th>
                        <th>Posledná zmena hesla</th>
                        <th className="w-1 text-center">Akcia</th>
                    </tr>
                </thead>
                <tbody>
                    {response?.data.map(user => (
                        <tr key={user.id}>
                            <td className="st-cell-strong">{user.displayName}</td>
                            <td className="st-cell-strong">{user.email}</td>
                            <td>
                                {user.isSuperAdmin ? (
                                    <span className="st-inline-flag st-inline-flag--warning">
                                        <FontAwesomeIcon icon={faShieldHalved} />
                                        Áno
                                    </span>
                                ) : (
                                    <span className="st-cell-muted-sm">Nie</span>
                                )}
                            </td>
                            <td className="st-cell-muted">
                                {user.websites.length > 0 ? user.websites.map(w => w.shortName).join(', ') : '–'}
                            </td>
                            <td>
                                {user.isPasswordTemporary ? (
                                    <span className="st-inline-flag st-inline-flag--danger">
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        Áno
                                    </span>
                                ) : (
                                    <span className="st-inline-flag st-inline-flag--muted">
                                        <FontAwesomeIcon icon={faTimesCircle} />
                                        Nie
                                    </span>
                                )}
                            </td>
                            <td className="st-cell-muted st-cell-nowrap">
                                {user.lastPasswordChangeAt
                                    ? new Date(user.lastPasswordChangeAt).toLocaleString('sk-SK', {
                                        year: 'numeric', month: '2-digit', day: '2-digit',
                                        hour: '2-digit', minute: '2-digit',
                                    })
                                    : '–'}
                            </td>
                            <td className="text-center">
                                <CustomTooltip placement="right" message="Upraviť">
                                    <Link to={`/user/edit/${user.id}`}>
                                        <EditButton color={action} iconOnly />
                                    </Link>
                                </CustomTooltip>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </TableShell>
    );
};

export default UserTableComponent;
